import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { ShieldCheck, CalendarX2, HelpCircle, CircleSlash, Loader2, ChevronRight, Link2, X } from "lucide-react";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, count, tone, children, hint }) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-6" data-testid={`health-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${tone}`}><Icon className="h-4 w-4" /></div>
      <div className="flex-1">
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      </div>
      <span className="text-sm font-bold text-slate-700 tabular-nums">{count}</span>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => (
  <div className="px-5 py-8 text-center text-sm text-slate-400">{text}</div>
);

export default function DataHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [editing, setEditing] = useState(null); // label being assigned
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/data-health")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    api.get("/loans").then((r) => setLoans(r.data.map((l) => ({
      code: l.code, customerCode: l.customerCode, customerName: l.customerName,
    })))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAssign = (label) => { setEditing(label); setPick(""); };
  const submitAssign = (label) => {
    const code = pick.trim();
    if (!code) { toast.error("Type or pick a loan code first"); return; }
    if (!loans.some((l) => l.code === code)) { toast.error(`Loan ${code} not found`); return; }
    setSaving(true);
    api.post("/reports/data-health/assign", { label, loanCode: code })
      .then((r) => {
        toast.success(`Attached ${r.data.assigned} payment(s) · ${fmtRM(r.data.total)} to ${code}`);
        setEditing(null); setPick(""); load();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Assign failed"))
      .finally(() => setSaving(false));
  };

  const recurring = (data?.unattributedCashIn || []).filter((g) => g.recurring);
  const oneOff = (data?.unattributedCashIn || []).filter((g) => !g.recurring);

  return (
    <Layout>
      <PageHeader
        title="Data Health"
        subtitle="Automatic checks that flag likely data-entry issues across the selected branch."
      />

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400" data-testid="health-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Running checks…
        </div>
      ) : !data ? (
        <Empty text="Could not load report." />
      ) : (
        <>
          {/* Unattributed cash-in */}
          <Section
            icon={HelpCircle}
            title="Unattributed Cash-In"
            count={`${data.unattributedCashIn.length} · ${fmtRM(data.unattributedTotal)}`}
            tone="bg-amber-50 text-amber-600"
            hint="Money logged as Capital In whose label matches no loan — recurring ones are likely payments filed under a misspelled name."
          >
            {data.unattributedCashIn.length === 0 ? (
              <Empty text="No unattributed cash-in. 🎉" />
            ) : (
              <table className="w-full text-sm" data-testid="unattributed-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                    <th className="px-5 py-2.5 font-medium">Label</th>
                    <th className="px-3 py-2.5 font-medium text-right">Entries</th>
                    <th className="px-3 py-2.5 font-medium text-right">Total</th>
                    <th className="px-3 py-2.5 font-medium">Period</th>
                    <th className="px-3 py-2.5 font-medium">Signal</th>
                    <th className="px-5 py-2.5 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[...recurring, ...oneOff].map((g) => (
                    <tr key={`${g.branch}-${g.description}`} className="border-b border-slate-50 hover:bg-slate-50 align-top">
                      <td className="px-5 py-3 font-medium text-slate-800">{g.description}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-600">{g.count}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-slate-900">{fmtRM(g.total)}</td>
                      <td className="px-3 py-3 text-xs text-slate-500">{g.firstDate} → {g.lastDate}</td>
                      <td className="px-3 py-3">
                        {g.recurring ? (
                          <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border bg-amber-50 text-amber-800 border-amber-200">Likely payment</span>
                        ) : (
                          <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border bg-slate-100 text-slate-600 border-slate-200">One-off</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {editing === g.description ? (
                          <div className="flex items-center gap-2 justify-end" data-testid={`assign-form-${g.description}`}>
                            <input
                              list="loan-codes"
                              value={pick}
                              onChange={(e) => setPick(e.target.value)}
                              placeholder="Loan code…"
                              data-testid="assign-loan-input"
                              className="w-44 px-2 py-1 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-slate-900 outline-none"
                              autoFocus
                            />
                            <button
                              onClick={() => submitAssign(g.description)}
                              disabled={saving}
                              data-testid="assign-confirm-button"
                              className="px-2.5 py-1 bg-slate-900 text-white rounded text-xs font-medium hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-1"
                            >
                              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} Attach
                            </button>
                            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openAssign(g.description)}
                            data-testid={`assign-open-${g.description}`}
                            className="text-xs font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 border border-slate-200 rounded-full px-2.5 py-1 hover:border-slate-400"
                          >
                            <Link2 className="h-3 w-3" /> Assign to loan
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <datalist id="loan-codes">
            {loans.map((l) => (
              <option key={l.code} value={l.code}>{`${l.customerCode || ""} · ${l.customerName || ""}`}</option>
            ))}
          </datalist>

          {/* Future-dated records */}
          <Section
            icon={CalendarX2}
            title="Future-Dated Records"
            count={data.futureDates.length}
            tone="bg-red-50 text-red-600"
            hint="Disbursements or payments dated after today (appointment dates are excluded)."
          >
            {data.futureDates.length === 0 ? (
              <Empty text="No future-dated records." />
            ) : (
              <table className="w-full text-sm" data-testid="future-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                    <th className="px-5 py-2.5 font-medium">Type</th>
                    <th className="px-3 py-2.5 font-medium">Reference</th>
                    <th className="px-3 py-2.5 font-medium">Customer</th>
                    <th className="px-5 py-2.5 font-medium text-right">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.futureDates.map((f, i) => (
                    <tr key={`${f.ref}-${f.date}-${i}`} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-5 py-3 text-slate-600">{f.kind}</td>
                      <td className="px-3 py-3">
                        <Link to={`/loans/${f.ref}`} className="text-slate-900 font-medium hover:underline">{f.ref}</Link>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{f.name || "—"}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-red-600 font-medium">{f.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Active loans with no payments */}
          <Section
            icon={CircleSlash}
            title="Active Loans Without Payments"
            count={data.noPaymentActive.length}
            tone="bg-slate-100 text-slate-600"
            hint="Active loans disbursed over 30 days ago with no recorded payment — possible missing or misfiled data."
          >
            {data.noPaymentActive.length === 0 ? (
              <Empty text="No such loans." />
            ) : (
              <table className="w-full text-sm" data-testid="nopayment-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                    <th className="px-5 py-2.5 font-medium">Loan</th>
                    <th className="px-3 py-2.5 font-medium">Customer</th>
                    <th className="px-3 py-2.5 font-medium">Applied</th>
                    <th className="px-3 py-2.5 font-medium text-right">Outstanding</th>
                    <th className="px-5 py-2.5 font-medium text-right">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.noPaymentActive.map((r) => (
                    <tr key={r.code} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-5 py-3">
                        <Link to={`/loans/${r.code}`} className="text-slate-900 font-medium hover:underline inline-flex items-center gap-1">
                          {r.code} <ChevronRight className="h-3 w-3 text-slate-300" />
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{r.name}</td>
                      <td className="px-3 py-3 text-slate-500">{r.dateApplied}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-slate-900">{fmtRM(r.outstanding)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-500">{r.daysSinceDisbursement}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
            <ShieldCheck className="h-3.5 w-3.5" /> Checks run live against the current data for the selected branch.
          </div>
        </>
      )}
    </Layout>
  );
}
